import React, { useMemo } from "react";

import { HelperText } from "@common/components/HelperText/HelperText";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Workspace } from "@common/models";
import { getBillableApps } from "@common/util/appsUtil";
import { quantity } from "@common/util/strings";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { getAppsLimitNumbers, LimitNumbers } from "@main/context/Subscription/SubscriptionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, Link } from "react-router-dom";

import * as Styled from "./AddAppLimitBanner.styles";

interface Props {
  limitNumbers: LimitNumbers;
  selectedWorkspace: Workspace;
}

export default function AddAppLimitBanner() {
  const { apps } = useAppContext();
  const { selectedWorkspace } = useAppSelectionContext();
  const activeApps = useMemo(() => getBillableApps(apps), [apps]);
  const limitNumbers = getAppsLimitNumbers(activeApps, selectedWorkspace?.subscription_plans ?? []);

  if (!limitNumbers || !selectedWorkspace) return null;

  if (limitNumbers.isBelowLimit) {
    return (
      <BelowLimitInfo
        limitNumbers={limitNumbers}
        selectedWorkspace={selectedWorkspace}
      />
    );
  }
  return (
    <LimitInfo
      limitNumbers={limitNumbers}
      selectedWorkspace={selectedWorkspace}
    />
  );
}

const BelowLimitInfo = ({ limitNumbers, selectedWorkspace }: Props) => {
  return (
    <Styled.Container>
      <HelperText>
        <Paragraph
          p2
          fontWeight={500}
        >
          {`Your plan allows ${limitNumbers.limit} active ${quantity(
            limitNumbers.limit,
            "app",
            "apps",
          )} — this will be your ${limitNumbers.nextNum}. Nothing to change now. See `}
          <Link
            to={generatePath(RoutePaths.ADMINISTRATION_BILLING, {
              workspaceSlug: selectedWorkspace?.slug,
            })}
          >
            Billing
          </Link>
          {` for plans and pricing.`}
        </Paragraph>
      </HelperText>
    </Styled.Container>
  );
};

const LimitInfo = ({ limitNumbers, selectedWorkspace }: Props) => {
  return (
    <Styled.Container>
      <HelperText>
        <Paragraph
          p2
          fontWeight={500}
        >
          {`Your plan allows ${limitNumbers.limit} active ${quantity(
            limitNumbers.limit,
            "app",
            "apps",
          )} — this would be your ${limitNumbers.nextNum}. Upgrade in `}
          <Link
            to={generatePath(RoutePaths.ADMINISTRATION_BILLING, {
              workspaceSlug: selectedWorkspace?.slug,
            })}
          >
            Billing
          </Link>
          {` or deactivate `}
          <Link
            to={generatePath(RoutePaths.ADMINISTRATION_APPS, {
              workspaceSlug: selectedWorkspace?.slug,
            })}
          >
            Apps
          </Link>
          {` you’re not using.`}
        </Paragraph>
      </HelperText>
    </Styled.Container>
  );
};
